import React, { useState, useEffect, useRef } from "react";
import { useSlideContext } from "./slideContext";

import {
  Box,
  Paper,
  Button,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import { Row, Col } from "react-bootstrap";
import interact from "interactjs";

export default function BlockTree() {
  const [expanded, setExpanded] = useState(false);
  const [activeSlideBlocks, setActiveSlideBlocks] = useState([]);
  const {
    currentEditorInstanceRef,
    currentSlideRef,
    registerBlockTreeUpdateFunction,
  } = useSlideContext();

  const listRef = useRef(null);
  const draggedElRef = useRef(null);
  const placeholderRef = useRef(null);

  // Load blocks
  useEffect(() => {
    if (
      currentEditorInstanceRef.current &&
      currentSlideRef.current &&
      currentEditorInstanceRef.current?.blocks
    ) {
      registerBlockTreeUpdateFunction(runUpdateActiveSlideBlocks);
      runUpdateActiveSlideBlocks();
    } else if (
      currentEditorInstanceRef.current === null &&
      currentSlideRef.current === null
    ) {
      setActiveSlideBlocks([]);
    }
  }, [currentEditorInstanceRef.current, currentSlideRef.current]);

  // Triggers changes done on slide
  const runUpdateActiveSlideBlocks = async () => {
    const editor = currentEditorInstanceRef.current;
    const slide = currentSlideRef.current;
    if (!editor || !slide) return;

    const blocks = editor.blocks;
    const blockLength = blocks?.getBlocksCount() ?? 0;

    const ids = await Promise.all(
      Array.from({ length: blockLength }, async (_, i) => {
        const block = blocks.getBlockByIndex(i);
        if (!block) return null;

        let toolboxEntry = null;
        try {
          toolboxEntry = await block.getActiveToolboxEntry(block.name);
        } catch {}

        const foundBlock = slide.querySelector(
          `.ce-block[data-id="${block.id}"]`,
        );

        return {
          id: block.id,
          name: block.name,
          toolboxLabel: toolboxEntry?.title || toolboxEntry?.name || block.name,
          toolboxIcon: toolboxEntry?.icon || null,
          groupId: foundBlock?.getAttribute("data-group") ?? null,
        };
      }),
    );

    normalizeGroupedBlocks(ids, editor);
    setActiveSlideBlocks(ids.filter(Boolean));
  };

  const normalizeGroupedBlocks = (_blocks, editor) => {
    const groupIds = [
      ...new Set(_blocks.map((block) => block.groupId).filter(Boolean)),
    ];

    for (const groupId of groupIds) {
      const groupedBlocks = _blocks.filter((b) => b.groupId === groupId);

      let startingIndex;

      const reversed = [...groupedBlocks].reverse();

      for (let index = 0; index < reversed.length; index++) {
        const groupedBlock = reversed[index];
        const blockIndex = _blocks.findIndex(
          (_block) => _block.id === groupedBlock.id,
        );

        if (index === 0) {
          startingIndex = blockIndex;
          continue;
        }

        if (startingIndex - index !== blockIndex) {
          editor.blocks.move(startingIndex - index, blockIndex);

          break;
        }
      }
    }
  };

  // Setup interact.js draggable behavior
  useEffect(() => {
    interact(".draggable").draggable({
      // inertia: true,
      // autoScroll: true,
      listeners: {
        start(event) {
          const el = event.target;
          draggedElRef.current = el;

          const rect = el.getBoundingClientRect();
          const listRect = listRef.current.getBoundingClientRect();

          // Adjust rect to be relative to the list container
          const left = rect.left - listRect.left + listRef.current.scrollLeft;
          const top = rect.top - listRect.top + listRef.current.scrollTop;

          // Create placeholder
          const placeholder = document.createElement("div");
          placeholder.className = "placeholder";
          placeholder.style.height = `${rect.height}px`;
          placeholderRef.current = placeholder;

          el.parentNode.insertBefore(placeholder, el.nextSibling);

          el.classList.add("dragging");
          el.style.width = `${rect.width}px`;
          el.style.left = `${left}px`;
          el.style.top = `${top}px`;
          el.style.position = "absolute";

          el.dataset.x = left;
          el.dataset.y = top;
        },

        move(event) {
          const el = draggedElRef.current;
          if (!el) return;

          let x = parseFloat(el.dataset.x) + event.dx;
          let y = parseFloat(el.dataset.y) + event.dy;

          el.style.left = `${x}px`;
          el.style.top = `${y}px`;
          el.dataset.x = x;
          el.dataset.y = y;

          const draggedRect = el.getBoundingClientRect();
          if (!el.classList.contains("grouped")) {
            const list = listRef.current;
            const children = [
              ...list.querySelectorAll(".draggable:not(.dragging, .grouped)"),
            ];

            for (const child of children) {
              const rect = child.getBoundingClientRect();
              const midpoint = rect.top + rect.height / 2;

              if (draggedRect.top < midpoint) {
                list.insertBefore(placeholderRef.current, child);
                return;
              }
            }

            list.appendChild(placeholderRef.current);
          } else {
            
          }
        },

        end() {
          const el = draggedElRef.current;
          const placeholder = placeholderRef.current;
          const list = listRef.current;

          if (!el || !placeholder) return;

          list.insertBefore(el, placeholder);

          // Update React state according to new DOM order
          const newOrder = [...list.children]
            .filter((n) => n.classList.contains("draggable")) //Removes placeholder from array
            .map((n) => activeSlideBlocks.find((b) => b.id === n.dataset.id)); //Constructs new order of array of objects from activeSlideBlocks using dataset id

          // setActiveSlideBlocks(newOrder.reverse());

          el.classList.remove("dragging");
          el.style.left = "";
          el.style.top = "";
          el.style.width = "";
          el.style.position = "";
          delete el.dataset.x;
          delete el.dataset.y;

          placeholder.remove();
          draggedElRef.current = null;
          placeholderRef.current = null;
        },
      },
    });

    return () => interact(".draggable").unset();
  }, [activeSlideBlocks]);

  const renderBlocks = () => {
    if (activeSlideBlocks.length > 0) {
      const tempActiveSlideBlocks = [...activeSlideBlocks];
      const orderedBlocks = [];
      const groupMap = {};

      for (const block of tempActiveSlideBlocks) {
        const { groupId } = block;

        if (!groupId) {
          orderedBlocks.push({ renderType: "block", ...block });
          continue;
        }

        if (!groupMap[groupId]) {
          const group = {
            groupId,
            renderType: "group",
            blocks: [],
          };

          groupMap[groupId] = group;
          orderedBlocks.push(group);
        }

        groupMap[groupId].blocks.push(block);
      }

      return (
        <>
          {orderedBlocks.map((element) =>
            element?.renderType === "group"
              ? GroupNode(element)
              : BlockNode(element),
          )}
        </>
      );
    }
  };

  const GroupNode = (blockGroup) => {
    return (
      <Accordion className="draggable group-node">
        <AccordionSummary>
          <Typography
            variant="caption"
            owner={"admin"}
            color={"clientText.dark"}
            weight="medium"
          >
            {blockGroup.groupId}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className="group-node-list">
          {blockGroup.blocks.map((block) => BlockNode(block, true))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const BlockNode = (block, grouped) => {
    return (
      <Box
        className={`block-tree-node draggable ${grouped ? "grouped" : ""}`}
        key={block.id}
        data-id={block.id}
        // data-index={_index}
      >
        <Row>
          <Col xs="auto" className="px-1">
            <div
              className="block-tree-node-icon"
              dangerouslySetInnerHTML={{
                __html: block.toolboxIcon || "",
              }}
            />
          </Col>
          <Col className="flex-grow-1 ps-0">
            <Typography
              variant="caption"
              owner={"admin"}
              color={"clientText.dark"}
              weight="medium"
            >
              {block.toolboxLabel ?? block.name}{" "}
              {block.groupId ? `(Group: ${block.groupId})` : ""}
            </Typography>
          </Col>
        </Row>
      </Box>
    );
  };

  return (
    <Box className={`block-tree-main-container ${expanded ? "expanded" : ""}`}>
      <Paper className="block-tree-paper-container" elevation={3}>
        <Tooltip title={"Block Tree"} placement="left">
          <Button
            className="block-tree-toggle-btn"
            onClick={() => setExpanded(!expanded)}
          >
            <AccountTreeRoundedIcon sx={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
        <Box className="block-tree-inner-container">
          <Box className="block-tree-header">
            <Typography variant="h6">Block Tree</Typography>
          </Box>
          <Box className="block-tree-list-container" ref={listRef}>
            {activeSlideBlocks?.length > 0 ? (
              renderBlocks()
            ) : (
              <Typography
                variant="body2"
                sx={{ padding: "10px", color: "#777" }}
              >
                No blocks available
              </Typography>
            )}
            {/* {activeSlideBlocks.length > 0 ? (
              [...activeSlideBlocks]
                .reverse()
                .map((block, index) => BlockNode(block, index))
            ) : (
              <Typography
                variant="body2"
                sx={{ padding: "10px", color: "#777" }}
              >
                No blocks available
              </Typography>
            )} */}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
